import {
  type ApartmentSheetRow,
  type SpaceKind,
  type SpaceSheetRow,
  spaceSheetResponseSchema,
  type MeResponse,
  apartmentSheetResponseSchema,
  type ProjectListResponse,
  type StructureListResponse,
  type UnitSummary,
  meResponseSchema,
  projectListResponseSchema,
  structureListResponseSchema,
  unitListResponseSchema,
} from '@sivan/shared';
import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { ApiError, apiGet } from '../api/client';

export type Loadable<T> =
  | { readonly state: 'loading' }
  | { readonly state: 'ready'; readonly data: T }
  | { readonly state: 'forbidden' }
  | { readonly state: 'error'; readonly requestId: string | null };

/** Loads `load()` whenever `key` changes; `reload` repeats it. Stale answers are ignored. */
export function useLoad<T>(key: string, load: () => Promise<T>): [Loadable<T>, () => void] {
  const [attempt, setAttempt] = useState(0);
  const request = `${key}#${String(attempt)}`;
  const [settled, setSettled] = useState<{ request: string; result: Loadable<T> } | null>(null);
  useEffect(() => {
    let current = true;
    load().then(
      (data) => {
        if (current) setSettled({ request, result: { state: 'ready', data } });
      },
      (error: unknown) => {
        if (!current) return;
        const result: Loadable<T> =
          error instanceof ApiError && [401, 403, 404].includes(error.status)
            ? { state: 'forbidden' }
            : { state: 'error', requestId: error instanceof ApiError ? error.requestId : null };
        setSettled({ request, result });
      },
    );
    return () => {
      current = false;
    };
    // `load` is recreated each render; `request` decides when to load again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request]);
  const reload = useCallback(() => {
    setAttempt((n) => n + 1);
  }, []);
  return [settled?.request === request ? settled.result : { state: 'loading' }, reload];
}

export interface Workspace {
  readonly me: MeResponse;
  readonly projects: ProjectListResponse['items'];
}

// The signed-in user and their projects, loaded once and shared by the shell and every screen
// (the navigation, the project switcher, Home…): a reload anywhere (a project added or renamed)
// reloads it for all of them.
let generation = 0;
let cached: {
  generation: number;
  promise: Promise<Workspace>;
  /** The loaded workspace as a state, one object for every screen (stable between renders). */
  ready: Loadable<Workspace> | null;
} | null = null;
const workspaceListeners = new Set<() => void>();

function loadWorkspace(of: number): Promise<Workspace> {
  if (cached?.generation !== of) {
    const entry: NonNullable<typeof cached> = {
      generation: of,
      ready: null,
      promise: Promise.all([
        apiGet('/me', meResponseSchema),
        apiGet('/projects?limit=100', projectListResponseSchema),
      ]).then(([me, projects]) => {
        const value = { me, projects: projects.items };
        entry.ready = { state: 'ready', data: value };
        return value;
      }),
    };
    cached = entry;
  }
  return cached.promise;
}

/** Loads the workspace again for every screen (after a change to the projects or access). */
export function reloadWorkspace(): void {
  generation += 1;
  for (const listener of workspaceListeners) listener();
}

/** Forgets the loaded workspace (on sign-out, and between tests). */
export function forgetWorkspace(): void {
  cached = null;
  generation += 1;
}

const subscribeWorkspace = (listener: () => void) => {
  workspaceListeners.add(listener);
  return () => {
    workspaceListeners.delete(listener);
  };
};

export function useWorkspace(): [Loadable<Workspace>, () => void] {
  const of = useSyncExternalStore(subscribeWorkspace, () => generation);
  const [state] = useLoad(`workspace:${String(of)}`, () => loadWorkspace(of));
  // Loaded (here or by another screen): the same state object every time, so screens comparing
  // it between renders see no change; shown at once, without a loading flash.
  const shared = cached?.generation === of ? cached.ready : null;
  if (shared !== null && state.state !== 'error' && state.state !== 'forbidden')
    return [shared, reloadWorkspace];
  return [state, reloadWorkspace];
}

/** Upper bound on units loaded into the grid at once (safety; far above the pilot's size). */
export const MAX_GRID_UNITS = 5000;

export interface ProjectData {
  readonly structures: StructureListResponse['items'];
  readonly units: UnitSummary[];
  readonly truncated: boolean;
}

export function useProjectData(
  projectId: string,
  version: number,
): [Loadable<ProjectData>, () => void] {
  return useLoad(`${projectId}:${String(version)}`, async () => {
    const structures = await apiGet(
      `/projects/${projectId}/structures`,
      structureListResponseSchema,
    );
    const units: UnitSummary[] = [];
    let total = Infinity;
    while (units.length < total && units.length < MAX_GRID_UNITS) {
      const page = await apiGet(
        `/projects/${projectId}/units?limit=100&offset=${String(units.length)}`,
        unitListResponseSchema,
      );
      total = page.page.total;
      units.push(...page.items);
      if (page.items.length === 0) break;
    }
    return { structures: structures.items, units, truncated: total > units.length };
  });
}

/** Business keys the actor holds on the project (UI gating only; the server decides). */
export function projectPermissions(me: MeResponse, projectId: string): ReadonlySet<string> {
  const specific = me.access.projects.find((p) => p.projectId === projectId)?.permissions ?? [];
  return new Set([...me.access.allProjects, ...specific]);
}

export interface ApartmentSheetData {
  readonly structures: StructureListResponse['items'];
  readonly apartments: ApartmentSheetRow[];
  readonly truncated: boolean;
  /** What the reader may do with the contact columns (DR-185), as the server decided. */
  readonly personal: { readonly view: boolean; readonly edit: boolean };
  /** What the reader may do with the note column (DR-187), as the server decided. */
  readonly note: { readonly view: boolean; readonly edit: boolean };
}

/** The Apartments sheet of a project: structures (for local checks) and every apartment row. */
export function useApartmentSheet(
  projectId: string,
  version: number,
): [Loadable<ApartmentSheetData>, () => void] {
  return useLoad(`sheet:${projectId}:${String(version)}`, async () => {
    const structures = await apiGet(
      `/projects/${projectId}/structures`,
      structureListResponseSchema,
    );
    const apartments: ApartmentSheetRow[] = [];
    let personal = { view: false, edit: false };
    let note = { view: false, edit: false };
    let total = Infinity;
    while (apartments.length < total && apartments.length < MAX_GRID_UNITS) {
      const page = await apiGet(
        `/projects/${projectId}/apartment-sheet?limit=100&offset=${String(apartments.length)}`,
        apartmentSheetResponseSchema,
      );
      total = page.page.total;
      personal = page.personal;
      note = page.note;
      apartments.push(...page.items);
      if (page.items.length === 0) break;
    }
    return {
      structures: structures.items,
      apartments,
      truncated: total > apartments.length,
      personal,
      note,
    };
  });
}

export interface SpaceSheetData {
  readonly structures: StructureListResponse['items'];
  readonly spaces: SpaceSheetRow[];
  readonly truncated: boolean;
}

/** The Parking or Storage sheet of a project: structures and every space of the kind. */
export function useSpaceSheet(
  projectId: string,
  kind: SpaceKind,
  version: number,
): [Loadable<SpaceSheetData>, () => void] {
  return useLoad(`spaces:${kind}:${projectId}:${String(version)}`, async () => {
    const structures = await apiGet(
      `/projects/${projectId}/structures`,
      structureListResponseSchema,
    );
    const spaces: SpaceSheetRow[] = [];
    let total = Infinity;
    while (spaces.length < total && spaces.length < MAX_GRID_UNITS) {
      const page = await apiGet(
        `/projects/${projectId}/space-sheet/${kind}?limit=100&offset=${String(spaces.length)}`,
        spaceSheetResponseSchema,
      );
      total = page.page.total;
      spaces.push(...page.items);
      if (page.items.length === 0) break;
    }
    return { structures: structures.items, spaces, truncated: total > spaces.length };
  });
}
